import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  LogOut, RefreshCw, Trash2, Phone, Mail, Inbox, CalendarCheck,
  CheckCircle2, LayoutDashboard, Loader2,
} from "lucide-react";
import { api, LOGO_URL } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const STATUS = {
  new: { label: "New", cls: "bg-blue-50 text-[#2B4BB8] border-blue-200" },
  scheduled: { label: "Scheduled", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  completed: { label: "Completed", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  cancelled: { label: "Cancelled", cls: "bg-slate-100 text-slate-500 border-slate-200" },
};

export default function AdminDashboard() {
  const { logout } = useAuth();
  const nav = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [stats, setStats] = useState({ total: 0, new: 0, scheduled: 0, completed: 0 });
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [b, s] = await Promise.all([api.get("/bookings"), api.get("/bookings/stats")]);
      setBookings(b.data);
      setStats(s.data);
    } catch (e) {
      toast.error("Failed to load bookings.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/bookings/${id}`, { status });
      toast.success(`Marked ${STATUS[status].label.toLowerCase()}.`);
      load();
    } catch { toast.error("Update failed."); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this booking?")) return;
    try {
      await api.delete(`/bookings/${id}`);
      toast.success("Deleted.");
      load();
    } catch { toast.error("Delete failed."); }
  };

  const doLogout = async () => { await logout(); nav("/admin/login"); };

  const filtered = filter === "all" ? bookings : bookings.filter((b) => b.status === filter);

  const CARDS = [
    { key: "total", label: "Total Requests", value: stats.total, icon: LayoutDashboard },
    { key: "new", label: "New", value: stats.new, icon: Inbox },
    { key: "scheduled", label: "Scheduled", value: stats.scheduled, icon: CalendarCheck },
    { key: "completed", label: "Completed", value: stats.completed, icon: CheckCircle2 },
  ];

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[#F8FAFC]">
      <header className="bg-white border-b border-[#E2E8F0] sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="logo" className="h-10 w-10 object-contain" />
            <span className="font-display font-bold text-[#1A2A5E]">Booking Dashboard</span>
          </div>
          <div className="flex items-center gap-2">
            <button data-testid="refresh-btn" onClick={load} className="flex items-center gap-2 text-sm font-semibold text-[#475569] hover:text-[#1A2A5E] px-3 py-2 rounded-sm hover:bg-[#F1F5F9]">
              <RefreshCw className="w-4 h-4" /> Refresh
            </button>
            <button data-testid="logout-btn" onClick={doLogout} className="flex items-center gap-2 text-sm font-semibold text-white bg-[#1A2A5E] hover:bg-[#2B4BB8] px-4 py-2 rounded-sm">
              <LogOut className="w-4 h-4" /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-5 lg:px-8 py-8">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {CARDS.map((c) => (
            <div key={c.key} data-testid={`stat-${c.key}`} className="bg-white border border-[#E2E8F0] rounded-sm p-5">
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold uppercase tracking-widest text-[#94A3B8]">{c.label}</span>
                <c.icon className="w-4 h-4 text-[#2B4BB8]" />
              </div>
              <div className="mt-3 font-display text-3xl font-extrabold text-[#0F172A]">{c.value}</div>
            </div>
          ))}
        </div>

        <div className="flex flex-wrap gap-2 mb-4">
          {["all", "new", "scheduled", "completed", "cancelled"].map((f) => (
            <button key={f} data-testid={`filter-${f}`} onClick={() => setFilter(f)}
              className={`text-sm font-semibold px-4 py-2 rounded-sm border transition ${
                filter === f ? "bg-[#1A2A5E] text-white border-[#1A2A5E]" : "bg-white text-[#475569] border-[#E2E8F0] hover:border-[#2B4BB8]"
              }`}>
              {f === "all" ? "All" : STATUS[f].label}
            </button>
          ))}
        </div>

        <div className="bg-white border border-[#E2E8F0] rounded-sm overflow-hidden">
          {loading ? (
            <div className="py-24 flex justify-center"><Loader2 className="w-8 h-8 animate-spin text-[#1A2A5E]" /></div>
          ) : filtered.length === 0 ? (
            <div data-testid="empty-state" className="py-24 text-center text-[#94A3B8]">
              <Inbox className="w-10 h-10 mx-auto mb-3" />
              No booking requests yet.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-[#F8FAFC] border-b border-[#E2E8F0] text-left text-xs uppercase tracking-widest text-[#94A3B8]">
                    <th className="px-4 py-3 font-bold">Customer</th>
                    <th className="px-4 py-3 font-bold">Service</th>
                    <th className="px-4 py-3 font-bold">Details</th>
                    <th className="px-4 py-3 font-bold">Received</th>
                    <th className="px-4 py-3 font-bold">Status</th>
                    <th className="px-4 py-3 font-bold text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((b) => (
                    <tr key={b.id} data-testid={`booking-row-${b.id}`} className="border-b border-[#F1F5F9] hover:bg-[#F8FAFC]/60 align-top">
                      <td className="px-4 py-4">
                        <div className="font-bold text-[#0F172A]">{b.name}</div>
                        <a href={`tel:${b.phone}`} className="flex items-center gap-1 text-xs text-[#475569] mt-1"><Phone className="w-3 h-3" />{b.phone}</a>
                        <a href={`mailto:${b.email}`} className="flex items-center gap-1 text-xs text-[#475569]"><Mail className="w-3 h-3" />{b.email}</a>
                      </td>
                      <td className="px-4 py-4">
                        <div className="font-semibold text-[#0F172A]">{b.service}</div>
                        <div className="text-xs text-[#475569]">{b.property_type}</div>
                      </td>
                      <td className="px-4 py-4 max-w-xs">
                        {b.address && <div className="text-xs text-[#475569]">📍 {b.address}</div>}
                        {b.preferred_date && <div className="text-xs text-[#475569]">🗓 {b.preferred_date}</div>}
                        {b.message && <div className="text-xs text-[#334155] mt-1 line-clamp-3">{b.message}</div>}
                      </td>
                      <td className="px-4 py-4 text-xs text-[#475569] whitespace-nowrap">
                        {new Date(b.created_at).toLocaleDateString()}<br />
                        {new Date(b.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                      </td>
                      <td className="px-4 py-4">
                        <span className={`inline-block text-xs font-bold px-2.5 py-1 rounded-sm border ${STATUS[b.status]?.cls || ""}`}>
                          {STATUS[b.status]?.label || b.status}
                        </span>
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex items-center justify-end gap-1">
                          <select data-testid={`status-select-${b.id}`} value={b.status} onChange={(e) => updateStatus(b.id, e.target.value)}
                            className="text-xs border border-[#E2E8F0] rounded-sm px-2 py-1.5 focus:outline-none focus:border-[#2B4BB8]">
                            <option value="new">New</option>
                            <option value="scheduled">Scheduled</option>
                            <option value="completed">Completed</option>
                            <option value="cancelled">Cancelled</option>
                          </select>
                          <button data-testid={`delete-btn-${b.id}`} onClick={() => remove(b.id)}
                            className="p-2 text-[#94A3B8] hover:text-red-600 hover:bg-red-50 rounded-sm">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
